/**
 * FAQ entries for the Insight Help dialog (header).
 * @typedef {{ qId: string, aId: string, defaultQ: string, defaultA: string }} InsightFaqItem
 */
export const INSIGHT_FAQ_ITEMS = [
  {
    qId: 'insightFaqQ001',
    aId: 'insightFaqA001',
    defaultQ: 'How do I create a new organization?',
    defaultA:
      'Navigate to Organizations, click "Add Organization", enter the organization name, address, contact information, and configure initial settings. Click Save to create the organization.'
  },
  {
    qId: 'insightFaqQ002',
    aId: 'insightFaqA002',
    defaultQ: 'Can I have multiple administrators for one organization?',
    defaultA:
      'Yes. Go to Administration > User within the organization and add users with appropriate roles (Secondary Admin, Insight Manager R/W, Insight Manager ReadOnly). Each user can have different permission levels.'
  },
  {
    qId: 'insightFaqQ003',
    aId: 'insightFaqA003',
    defaultQ: 'How do I edit organization details?',
    defaultA:
      'Navigate to Organization > Details, click Edit, modify the organization name, address, contact information, or settings as needed, and save your changes.'
  },
  {
    qId: 'insightFaqQ004',
    aId: 'insightFaqA004',
    defaultQ: 'How do I delete an organization?',
    defaultA:
      'Navigate to Organization Settings and select Delete Organization. Note: You must first remove or unclaim all devices and delete any child networks before deletion. This action is irreversible.'
  },
  {
    qId: 'insightFaqQ005',
    aId: 'insightFaqA005',
    defaultQ: 'What is Organization Summary?',
    defaultA:
      'Organization Summary provides an overview of the organization including total devices, connected clients, network health, recent activity, and key metrics across all networks within that organization.'
  },
  {
    qId: 'insightFaqQ006',
    aId: 'insightFaqA006',
    defaultQ: 'How can I view all devices across my organization?',
    defaultA:
      'From the Organization view, navigate to Devices to see a consolidated list of all devices across all networks within the organization. You can filter by device type, status, or network.'
  },
  {
    qId: 'insightFaqQ007',
    aId: 'insightFaqA007',
    defaultQ: 'How do I add a new SSID (Wi-Fi network)?',
    defaultA: `To add a new SSID:
    • Navigate Wi-Fi from the Location menu
    • Select SSID
    • Click the "Add " button
    • Configure the SSID name, security type (Open, WPA2, WPA3, etc.), password, and VLAN assignment
    • Optionally set up SSID schedules to control when the network is active
    • Save your configuration - the changes will be pushed to your access points`
  },
  {
    qId: 'insightFaqQ008',
    aId: 'insightFaqA008',
    defaultQ: 'What security modes are supported for SSIDs?',
    defaultA: `The portal supports:
    • Open (no security)
    • WPA2-Personal (PSK)
    • WPA3-Personal (SAE)
    • WPA2/WPA3 Mixed Mode
    • WPA2-Enterprise (802.1X with RADIUS)
    • WPA3-Enterprise
    • Enhanced Open (OWE)`
  },
  {
    qId: 'insightFaqQ009',
    aId: 'insightFaqA009',
    defaultQ: 'How do I set up SSID broadcast schedules?',
    defaultA:
      'Navigate Wi-Fi > Schedules, create a new schedule with specific days and time ranges, then assign it to your SSID in SSID Menu. The SSID will automatically enable/disable based on the schedule.'
  },
  {
    qId: 'insightFaqQ010',
    aId: 'insightFaqA010',
    defaultQ: 'What is MPSK (Multiple Pre-Shared Key) and how do I configure it?',
    defaultA:
      'MPSK allows multiple unique passwords for a single SSID, useful for assigning different keys to different users or devices. Go to Wi-Fi > MPSK Settings, enable MPSK for your SSID, and add individual keys with optional VLAN assignments and expiration dates.'
  },
  {
    qId: 'insightFaqQ011',
    aId: 'insightFaqA011',
    defaultQ: 'How do I change Wi-Fi channel or radio settings?',
    defaultA:
      'Go to Wi-Fi > Advance > Wireless Settings, Configure Radio Mode, channel width (20/40 MHz) and other configurations'
  },
  {
    qId: 'insightFaqQ012',
    aId: 'insightFaqA012',
    defaultQ: 'Can I hide my SSID from being visible to clients?',
    defaultA:
      'Yes. In Wi-Fi > SSID, edit your SSID and set "Broadcast SSID" to Off. Clients will need to manually enter the network name to connect.'
  },
  {
    qId: 'insightFaqQ013',
    aId: 'insightFaqA013',
    defaultQ: 'How do I enable band steering?',
    defaultA:
      'Navigate to Wi-Fi >SSID, edit your SSID, and enable Band Steering. This encourages dual-band capable clients to connect to the less congested 5GHz band for better performance.'
  },
  {
    qId: 'insightFaqQ014',
    aId: 'insightFaqA014',
    defaultQ: 'How can I limit bandwidth for specific SSIDs?',
    defaultA:
      'Navigate to Wi-Fi >SSID, edit your SSID, configure rate limiting by setting maximum upload and download speeds per client or per SSID. This prevents any single network or user from consuming excessive bandwidth.'
  },
  {
    qId: 'insightFaqQ015',
    aId: 'insightFaqA015',
    defaultQ: 'What is Instant Wi-Fi?',
    defaultA:
      'Instant Wi-Fi provides quick network setup for temporary or simple deployments. Access it via Wi-Fi > Instant WiFi Menu. It is ideal for events, pop-up locations, or rapid deployment scenarios.'
  },
  {
    qId: 'insightFaqQ016',
    aId: 'insightFaqA016',
    defaultQ: 'How do I configure a guest Wi-Fi network?',
    defaultA:
      'Create a new SSID designated for guests, configure it with appropriate security, select "Customize" and click on "Add Captive Portal". Customize the splash page and optionally set bandwidth limits and session timeouts for guest users.'
  },
  {
    qId: 'insightFaqQ017',
    aId: 'insightFaqA017',
    defaultQ: 'How do I assign a VLAN to an SSID?',
    defaultA:
      'Navigate to Wi-Fi >SSID, edit your SSID and specify the VLAN ID and click on "Add VLAN ID". Traffic from clients on that SSID will be tagged with the specified VLAN.'
  },
  {
    qId: 'insightFaqQ018',
    aId: 'insightFaqA018',
    defaultQ: 'What is Management VLAN?',
    defaultA:
      'Management VLAN is the VLAN used for device management traffic. Configure it under Wi-Fi >SSID, edit your SSID specify the VLAN ID and click on "Add VLAN ID" to separate management traffic from user data traffic for enhanced security.'
  },
  {
    qId: 'insightFaqQ019',
    aId: 'insightFaqA019',
    defaultQ: 'How do I configure client isolation?',
    defaultA:
      'Navigate to Wi-Fi >SSID, edit your SSID and Enable Client Isolation toggle button. This prevents wireless clients on the same SSID from communicating directly with each other, improving security especially for guest networks.'
  },
  {
    qId: 'insightFaqQ020',
    aId: 'insightFaqA020',
    defaultQ: 'What are Mobile Profiles?',
    defaultA:
      'Mobile Profiles are policy templates that control device behavior and access. They include settings for Wi-Fi configuration, cellular data policies, schedule-based access rules, port filtering, domain restrictions, and device control policies.'
  },
  {
    qId: 'insightFaqQ021',
    aId: 'insightFaqA021',
    defaultQ: 'How do I create a Mobile Profile?',
    defaultA: `• Navigate to Wi-Fi > Mobile Profiles
• Click "Add Profile"
• Configure settings in each tab:
  - General: Profile name and basic settings
  - Wi-Fi: Wireless configuration and restrictions
  - Cellular: Mobile data policies
  - Router: Gateway settings
  - Schedule: Time-based access rules
  - Port Filtering: TCP/UDP port restrictions
  - Manage Restrictions: Domain/URL filtering
  - Device Control: Device-specific policies
• Save and apply to devices`
  },
  {
    qId: 'insightFaqQ022',
    aId: 'insightFaqA022',
    defaultQ: 'How can I configure VLAN settings for my network?',
    defaultA: `VLAN configuration is available under the Wired section:
    • Navigate to Wired > VLAN/Network Setup
    • Click "Add" to add a new VLAN
    • Specify the VLAN ID (1-4094), name, and IP settings
    • Assign the VLAN to specific switch ports as needed`
  },
  {
    qId: 'insightFaqQ023',
    aId: 'insightFaqA023',
    defaultQ: 'How do I set up PoE (Power over Ethernet) schedules?',
    defaultA: `PoE schedules allow you to control when power is delivered to connected devices:
    • Navigate to Wired > PoE Downtime Schedules
    • Click "Add Schedule" to create a new downtime schedule
    • Select the switch ports you want to manage
    • Define the schedule timing (days and hours)
    • Save the schedule - PoE will automatically turn off during scheduled downtime`
  },
  {
    qId: 'insightFaqQ024',
    aId: 'insightFaqA024',
    defaultQ: 'How do I configure Link Aggregation (LAG)?',
    defaultA:
      'Navigate to Wired > LAG, click "Add LAG", select the ports to bundle together (typically 2-8 ports), choose the LAG mode (LACP or Static), and save. LAGs increase bandwidth and provide redundancy.'
  },
  {
    qId: 'insightFaqQ025',
    aId: 'insightFaqA025',
    defaultQ: 'How do I edit an existing LAG?',
    defaultA:
      'In Wired > LAG, find the LAG you want to modify, click Edit, adjust the port membership or settings, and save your changes.'
  },
  {
    qId: 'insightFaqQ026',
    aId: 'insightFaqA026',
    defaultQ: 'What Spanning Tree Protocol modes are supported?',
    defaultA: `Go to Wired > Spanning Tree to configure STP. Supported modes include:
    • STP (802.1D) - Classic Spanning Tree
    • RSTP (802.1w) - Rapid Spanning Tree
    • MSTP (802.1s) - Multiple Spanning Tree
You can set bridge priority and configure port-specific settings.`
  },
  {
    qId: 'insightFaqQ027',
    aId: 'insightFaqA027',
    defaultQ: 'How do I configure RADIUS server configuration?',
    defaultA: `Navigate to Settings > RADIUS Configuration:
    • Add your RADIUS server IP address and port
    • Enter the shared secret
    • Configure Reauthentication Time
    • Enable 802.1X on specific switch ports`
  },
  {
    qId: 'insightFaqQ028',
    aId: 'insightFaqA028',
    defaultQ: 'How do I enable or disable switch ports?',
    defaultA:
      'Navigate the device details for your switch, go to Port Configuration, and toggle the administrative state of individual ports to enable or disable them.'
  },
  {
    qId: 'insightFaqQ029',
    aId: 'insightFaqA029',
    defaultQ: 'How do I configure port speed and duplex?',
    defaultA:
      'In switch port configuration, select the port and configure speed (Auto, 10M, 100M, 1G, 10G) and duplex (Auto, Full, Half). Auto-negotiation is recommended unless it is connected to legacy devices.'
  },
  {
    qId: 'insightFaqQ030',
    aId: 'insightFaqA030',
    defaultQ: 'How can I view and manage connected clients?',
    defaultA: `To view connected clients:
    • Click Clients in the main navigation menu
    • View a list of all connected devices with MAC address, IP, SSID/port, signal strength, and data usage
    • Filter by connection type (Wi-Fi or Wired), SSID, or VLAN
    • Click on individual clients to view detailed information`
  },
  {
    qId: 'insightFaqQ031',
    aId: 'insightFaqA031',
    defaultQ: 'What information is available about connected clients?',
    defaultA: `Client details include:
    • MAC address and hostname
    • IP address (IPv4/IPv6)
    • Connected SSID or switch port
    • Signal strength and SNR (for Wi-Fi)
    • Connection duration
    • Data usage (upload/download)
    • Device type/OS (when available)
    • Connection history`
  },
  {
    qId: 'insightFaqQ032',
    aId: 'insightFaqA032',
    defaultQ: 'How do I view client connection history?',
    defaultA:
      'In the clients list click on the client to see the detail view, then open the "Roaming History" tab to see connection/disconnection events over time.'
  },
  {
    qId: 'insightFaqQ033',
    aId: 'insightFaqA033',
    defaultQ: 'How do I add a new device to the portal?',
    defaultA: `Devices can be added by:
    • Scanning the device QR code using the mobile app
    • Entering the serial number manually in Devices > Add`
  },
  {
    qId: 'insightFaqQ034',
    aId: 'insightFaqA034',
    defaultQ: 'How do I view device details?',
    defaultA:
      'Navigate to Location Menu >Devices, click on any device to view detailed information including model, serial number, firmware version, uptime, connected clients, port status, and configuration settings.'
  },
  {
    qId: 'insightFaqQ035',
    aId: 'insightFaqA035',
    defaultQ: 'How can I reboot a device remotely?',
    defaultA:
      'Navigate to Devices, click on the device to get device dashboard, and click the "Reboot" button. Confirm the reboot. You can also schedule reboots or reboot multiple devices simultaneously.'
  },
  {
    qId: 'insightFaqQ036',
    aId: 'insightFaqA036',
    defaultQ: 'What do device health indicators mean?',
    defaultA: `• Green/Online: Device is connected and functioning normally
• Yellow/Warning: Device has minor issues or degraded performance
• Red/Offline: Device is unreachable or experiencing critical issues
• Gray/Inactive: Device is registered but not activated`
  },
  {
    qId: 'insightFaqQ037',
    aId: 'insightFaqA037',
    defaultQ: 'How do I view device logs?',
    defaultA:
      'Select the device in Devices and click on the device to navigate to device dashboard, then select the Troubleshoot tab to access the subtab to view system logs, run connectivity tests, and download diagnostic bundles for troubleshooting.'
  },
  {
    qId: 'insightFaqQ038',
    aId: 'insightFaqA038',
    defaultQ: 'What are Device Groups?',
    defaultA:
      'Device Groups allow you to organize devices and apply configurations to multiple devices simultaneously. Create groups based on location, function, or any criteria relevant to your deployment.'
  },
  {
    qId: 'insightFaqQ039',
    aId: 'insightFaqA039',
    defaultQ: 'How do I create a Device Group?',
    defaultA:
      'Navigate to Settings > Device Groups, click "Add", name the group, select the devices to include, and save. You can then apply configurations or perform actions on all devices in the group.'
  },
  {
    qId: 'insightFaqQ040',
    aId: 'insightFaqA040',
    defaultQ: 'How do I view Device Group Dashboard?',
    defaultA:
      'Navigate to Settings > Device Groups, select a group, and view the group dashboard showing aggregate statistics, health status, and metrics for all devices in that group.'
  },
  {
    qId: 'insightFaqQ041',
    aId: 'insightFaqA041',
    defaultQ: 'How do I configure Device Group Settings?',
    defaultA:
      'Device Group Settings, configure shared settings that apply to all devices in the group, including SSID assignments, VLAN configurations, and radio settings.'
  },
  {
    qId: 'insightFaqQ042',
    aId: 'insightFaqA042',
    defaultQ: 'How do I whitelist or blacklist MAC addresses?',
    defaultA:
      'In Wi-Fi > SSID, edit your SSID, below the MAC ACL option click on Add Devices and add at least one connected device and then enable the MAC ACL. Later you can add MAC address to the list and set the policy (whitelist) to permit only specific devices or blocklists (blacklist) to deny specific devices access to the network.'
  },
  {
    qId: 'insightFaqQ043',
    aId: 'insightFaqA043',
    defaultQ: 'How can I monitor network events and alerts?',
    defaultA: `The portal provides event monitoring capabilities:
    • Click Alarms in the Location menu
    • View real-time and historical events
    • Filter events by type, severity, device, time range
    • Events include device connectivity changes, configuration updates, security alerts, and client activity`
  },
  {
    qId: 'insightFaqQ044',
    aId: 'insightFaqA044',
    defaultQ: 'What types of events are logged?',
    defaultA: `Events include:
    • Device online/offline status changes
    • Configuration changes and updates
    • Client connection/disconnection
    • Security events and alerts
    • Firmware update status
    • Authentication successes and failures
    • System errors and warnings`
  },
  {
    qId: 'insightFaqQ045',
    aId: 'insightFaqA045',
    defaultQ: 'How do I filter events by type or severity?',
    defaultA:
      'Use the filter options in the Alarms view. Select specific event types (Critical, Warning, Info), Organization, Location, or time ranges.'
  },
  {
    qId: 'insightFaqQ046',
    aId: 'insightFaqA046',
    defaultQ: 'How do I add additional administrator users?',
    defaultA:
      'Go to Administration > Users, click "Add", enter the user\'s email, assign a role (Secondary Admin, Insight Manager Read/write, Insight Manager Read only), and specify which organizations/networks they can access.'
  },
  {
    qId: 'insightFaqQ047',
    aId: 'insightFaqA047',
    defaultQ: 'How do I change organization-wide settings?',
    defaultA:
      'Navigate to Administration to access organization-wide settings including user management, Management Subscription.'
  },
  {
    qId: 'insightFaqQ048',
    aId: 'insightFaqA048',
    defaultQ: 'How do I update firmware on my devices?',
    defaultA:
      'Go to Firmware (Settings > Firmware), select one or more devices, and initiate a firmware upgrade. You can upgrade immediately or schedule updates for maintenance windows.'
  },
  {
    qId: 'insightFaqQ049',
    aId: 'insightFaqA049',
    defaultQ: 'Can I schedule firmware updates?',
    defaultA:
      'Yes. Go to Settings > Firmware > Schedule Upgrade, Toggle to enable select the scheduled option and choose the date and time for the update.'
  },
  {
    qId: 'insightFaqQ050',
    aId: 'insightFaqA050',
    defaultQ: 'How do I check available firmware versions?',
    defaultA:
      'Go to Settings > Firmware > Target Firmware, for each device model under Target Version column you can select one in available 3 versions (latest at the top and previous 2 version).'
  },
  {
    qId: 'insightFaqQ051',
    aId: 'insightFaqA051',
    defaultQ: 'Can I update multiple devices at once?',
    defaultA:
      'Yes. Select multiple devices and click Update to initiate a batch firmware upgrade. You can also update all devices in a Device Group simultaneously.'
  },
  {
    qId: 'insightFaqQ052',
    aId: 'insightFaqA052',
    defaultQ: 'What happens during a firmware update?',
    defaultA:
      'During firmware update, the device downloads the new firmware, installs it, and reboots. The device will be offline briefly during the reboot. Clients will disconnect temporarily and reconnect after the update is completed.'
  },
  {
    qId: 'insightFaqQ053',
    aId: 'insightFaqA053',
    defaultQ: 'Can I roll back to the previous firmware version?',
    defaultA:
      'Rollback capability depends on the device model and firmware versions. Check the device documentation or contact support for rollback options.'
  },
  {
    qId: 'insightFaqQ054',
    aId: 'insightFaqA054',
    defaultQ: 'How do I add a new location?',
    defaultA:
      'From the Organization, navigate to Locations, click "Add ", enter the location name, and other details.'
  },
  {
    qId: 'insightFaqQ055',
    aId: 'insightFaqA055',
    defaultQ: 'Can I view location-specific dashboards?',
    defaultA:
      'Yes. Select a specific location to view its dashboard with metrics, device status, and client information specific to that location.'
  },
  {
    qId: 'insightFaqQ056',
    aId: 'insightFaqA056',
    defaultQ: 'A device shows offline. What should I check?',
    defaultA: `Verify the following:
    • Physical connectivity (power, network cables)
    • Network connectivity to the cloud
    • Firewall rules allowing outbound connections
    • DNS resolution on the network
    • Device has valid IP configuration
Try rebooting the device if it was previously working.`
  },
  {
    qId: 'insightFaqQ057',
    aId: 'insightFaqA057',
    defaultQ: 'Clients cannot connect to Wi-Fi. What should I check?',
    defaultA: `Verify:
    • SSID is enabled and broadcasting
    • Correct password is being used
    • Security mode matches client capabilities
    • VLAN and DHCP are configured correctly
    • No MAC filtering blocking the client
    • Schedule is not currently blocking the SSID`
  },
  {
    qId: 'insightFaqQ058',
    aId: 'insightFaqA058',
    defaultQ: 'Configuration changes are not applying. What should I do?',
    defaultA: `Check:
    • Device is online and connected to cloud
    • Configuration was saved successfully
    • No pending firmware updates blocking configuration
Try manually syncing the configuration or rebooting the device.`
  },
  {
    qId: 'insightFaqQ059',
    aId: 'insightFaqA059',
    defaultQ: 'Clients are getting incorrect IP addresses. What should I check?',
    defaultA: `Verify:
    • DHCP server is operational
    • DHCP pool is not exhausted
    • Correct VLAN assignment
    • DHCP Relay is configured if using remote DHCP server
    • No rogue DHCP servers (enable DHCP Snooping)`
  },
  {
    qId: 'insightFaqQ060',
    aId: 'insightFaqA060',
    defaultQ: 'How do I run diagnostics on a device?',
    defaultA:
      'Select the device in Devices, access Diagnostics or Troubleshooting tools. Run ping tests, traceroutes, or download diagnostic bundles for detailed analysis.'
  },
  {
    qId: 'insightFaqQ061',
    aId: 'insightFaqA061',
    defaultQ: 'Performance is slow. What should I check?',
    defaultA: `Investigate:
    • Channel congestion (try different channels)
    • Client distribution across APs
    • Bandwidth limits in place
    • Too many clients on single AP
    • Interference from neighboring networks
    • Enable band steering to distribute load`
  },
  {
    qId: 'insightFaqQ062',
    aId: 'insightFaqA062',
    defaultQ: 'How do I factory reset a device?',
    defaultA:
      'Physical reset can be done using the reset button on the device (hold for 10+ seconds). In the portal, you can use the "Factory Reset" option in the device dashboard, which will erase all configuration and unclaim the device.'
  },
  {
    qId: 'insightFaqQ063',
    aId: 'insightFaqA063',
    defaultQ: 'The portal is slow or not loading. What should I try?',
    defaultA: `• Clear browser cache and cookies
    • Try a different browser
    • Check your internet connection
    • Disable browser extensions that might interfere
    • Check the system status page for any known outages`
  },
  {
    qId: 'insightFaqQ064',
    aId: 'insightFaqA064',
    defaultQ: 'What subscription models are available in Insight?',
    defaultA: `Insight supports the following subscription models:
    • Monthly (Postpaid) Billing
    • 1 Year (Prepaid)
    • 3 Years (Prepaid)
    • NETGEAR Partner (License Based)`
  },
  {
    qId: 'insightFaqQ065',
    aId: 'insightFaqA065',
    defaultQ: 'What plans can I purchase?',
    defaultA: `You can purchase:
    • Monthly (Postpaid) Billing
    • 1 Year (Prepaid)
    • 3 Years (Prepaid)
    • NETGEAR Partner (License Based)`
  },
  {
    qId: 'insightFaqQ066',
    aId: 'insightFaqA066',
    defaultQ: 'Can I use multiple subscription types together?',
    defaultA: `No. You can only use one type at a time:
Monthly
OR Yearly (In yearly either 1 year or 3 year)
OR NETGEAR Partner (License Based)`
  },
  {
    qId: 'insightFaqQ067',
    aId: 'insightFaqA067',
    defaultQ: 'Can I change my plan?',
    defaultA: `For NETGEAR Partner: Users must contact support to change from NETGEAR Partner to other plans, as it is a license-based subscription model.
For Yearly (Prepaid) and Monthly (Postpaid): You can change between plans (Yearly, Monthly, or NETGEAR Partner) only after the current subscription expires.
Only for Yearly subscriptions you can upgrade from 1 year to 3 years, where user will be charged on a pro-rated basis, but for other subscription models we can move only after current subscription expires.`
  },
  {
    qId: 'insightFaqQ068',
    aId: 'insightFaqA068',
    defaultQ: 'Can I cancel the purchased subscription (Monthly and Yearly)?',
    defaultA: `On cancelling purchase the Auto-renew is disabled
Plan continues until billing period ends`
  },
  {
    qId: 'insightFaqQ069',
    aId: 'insightFaqA069',
    defaultQ: 'Who is eligible for trial?',
    defaultA: 'Trial is supported for all newly created accounts.'
  },
  {
    qId: 'insightFaqQ070',
    aId: 'insightFaqA070',
    defaultQ: 'What is the trial duration?',
    defaultA: 'Trial lasts for 3 months.'
  },
  {
    qId: 'insightFaqQ071',
    aId: 'insightFaqA071',
    defaultQ: 'What is the device limit for trial?',
    defaultA:
      'Trial supports up to 500 devices. A bundled device does not require any additional plan for the remainder of its term.'
  },
  {
    qId: 'insightFaqQ072',
    aId: 'insightFaqA072',
    defaultQ: 'What happens when I purchase a subscription during trial?',
    defaultA: 'Trial ends immediately and paid subscription becomes active.'
  },
  {
    qId: 'insightFaqQ073',
    aId: 'insightFaqA073',
    defaultQ: 'Which countries support Monthly (Postpaid) Billing?',
    defaultA: `Currently supported only in:
    • United States of America
    • Canada`
  },
  {
    qId: 'insightFaqQ074',
    aId: 'insightFaqA074',
    defaultQ: 'Which countries support Yearly (Prepaid)?',
    defaultA:
      'Supported in selected countries including:\nUnited States of America, Canada, Denmark, Ireland, UK, Japan, Norway, Luxembourg, Austria, Bulgaria, Germany, New Zealand, France, Hungary, Netherlands, Portugal, Czech Republic, Singapore, Australia, Croatia, Greece, Hong Kong, Poland, Slovakia, Belgium, Lithuania, Estonia, Finland, Latvia, Romania, Slovenia, Italy, Malta, Cyprus, South Africa, Spain, Switzerland'
  },
  {
    qId: 'insightFaqQ075',
    aId: 'insightFaqA075',
    defaultQ: 'What if my country does not support prepaid or postpaid?',
    defaultA: 'Only NETGEAR Partner (License Based) subscription is supported.'
  },
  {
    qId: 'insightFaqQ076',
    aId: 'insightFaqA076',
    defaultQ: 'Can I activate NETGEAR Partner while Monthly/Yearly is active?',
    defaultA: 'No. Existing subscription must expire first.'
  },
  {
    qId: 'insightFaqQ077',
    aId: 'insightFaqA077',
    defaultQ: 'Can I move back to Monthly after Yearly?',
    defaultA: 'Yes, but only after Yearly expires.'
  },
  {
    qId: 'insightFaqQ078',
    aId: 'insightFaqA078',
    defaultQ: 'How do credits work?',
    defaultA: `Credits can be added at account level or dedicated to one organization after purchase
Each managed device consumes one credit`
  },
  {
    qId: 'insightFaqQ079',
    aId: 'insightFaqA079',
    defaultQ: 'Where are credits stored?',
    defaultA: `Credits exist at:
Account level (available pool)
Organization level (allocated credits)`
  },
  {
    qId: 'insightFaqQ080',
    aId: 'insightFaqA080',
    defaultQ: 'Can I manually allocate credits?',
    defaultA:
      'Yes, User can manually allocate the credits using the Allocate option under Organization pool table'
  },
  {
    qId: 'insightFaqQ081',
    aId: 'insightFaqA081',
    defaultQ: 'Can I deallocate credits?',
    defaultA: 'Yes, but only unused credits can be moved back to account.'
  },
  {
    qId: 'insightFaqQ082',
    aId: 'insightFaqA082',
    defaultQ: 'What is Auto Allocation?',
    defaultA: 'Auto Allocation automatically assigns the credits as needed.'
  },
  {
    qId: 'insightFaqQ083',
    aId: 'insightFaqA083',
    defaultQ: 'What happens when I am onboard a device?',
    defaultA: `If credit available → device becomes Managed
If not → device becomes Unmanaged
In case of Bundle Device (HB) → It will be in managed state`
  },
  {
    qId: 'insightFaqQ084',
    aId: 'insightFaqA084',
    defaultQ: 'What happens when a device is deleted?',
    defaultA: 'Credit is automatically returned to organization pool.'
  },
  {
    qId: 'insightFaqQ085',
    aId: 'insightFaqA085',
    defaultQ: 'What happens when a device loses valid credit?',
    defaultA: 'Device moves to Unmanaged state.'
  },
  {
    qId: 'insightFaqQ086',
    aId: 'insightFaqA086',
    defaultQ: 'What does Unmanaged State mean?',
    defaultA: `Device remains connected to internet
Device is NOT connected to Insight Cloud`
  },
  {
    qId: 'insightFaqQ087',
    aId: 'insightFaqA087',
    defaultQ: 'How do I restore a device to managed state?',
    defaultA: `You must:
Add valid credit
Select device
Move from Unmanaged → Managed
Device will reboot and reconnect to Insight Cloud.`
  },
  {
    qId: 'insightFaqQ088',
    aId: 'insightFaqA088',
    defaultQ: 'What is the grace period for Yearly (Prepaid)?',
    defaultA: '30 days.'
  },
  {
    qId: 'insightFaqQ089',
    aId: 'insightFaqA089',
    defaultQ: 'What is the grace period for NETGEAR Partner (License Based)?',
    defaultA: '30 days.'
  },
  {
    qId: 'insightFaqQ090',
    aId: 'insightFaqA090',
    defaultQ: 'What is the grace period for Monthly (Postpaid)?',
    defaultA: '14 days.'
  },
  {
    qId: 'insightFaqQ091',
    aId: 'insightFaqA091',
    defaultQ: 'What is Expiring Soon?',
    defaultA: 'A status shown before expiry.'
  },
  {
    qId: 'insightFaqQ092',
    aId: 'insightFaqA092',
    defaultQ: 'When does Expiring Soon appear?',
    defaultA: `Starts 30 days before expiry
Applies only to:
Yearly (Prepaid)
NETGEAR Partner (License Based)`
  },
  {
    qId: 'insightFaqQ093',
    aId: 'insightFaqA093',
    defaultQ: 'Does Monthly Postpaid supports Expiring Soon?',
    defaultA:
      'Yes, Monthly has 5 days expiring soon will be shown when the Auto renewal is OFF.'
  },
  {
    qId: 'insightFaqQ094',
    aId: 'insightFaqA094',
    defaultQ: 'What happens after grace period ends?',
    defaultA:
      'Device moves to Unmanaged until valid credit is added and manually get moved to managed state.'
  },
  {
    qId: 'insightFaqQ095',
    aId: 'insightFaqA095',
    defaultQ: 'What does the Subscription card show?',
    defaultA: `Allocated: Number of credits in account level
Unallocated: Number of credits in account level
Scheduled: Number of credits scheduled in account / organization level`
  },
  {
    qId: 'insightFaqQ096',
    aId: 'insightFaqA096',
    defaultQ: 'What does Expiration card show?',
    defaultA: `Devices Count in Grace
Devices Count in Expiring Soon
Devices Count in Expired`
  },
  {
    qId: 'insightFaqQ097',
    aId: 'insightFaqA097',
    defaultQ: 'What does Current Plan show?',
    defaultA: 'Active subscription type for the account.'
  },
  {
    qId: 'insightFaqQ098',
    aId: 'insightFaqA098',
    defaultQ: 'What is License Scheduling?',
    defaultA: 'Allow users to activate licenses at a future date.'
  },
  {
    qId: 'insightFaqQ099',
    aId: 'insightFaqA099',
    defaultQ: 'Can I modify scheduled licenses?',
    defaultA: 'Yes, before activation.'
  },
  {
    qId: 'insightFaqQ100',
    aId: 'insightFaqA100',
    defaultQ: 'Can I delete a scheduled license?',
    defaultA:
      'Yes, User can delete the schedule licenses, which will get removed from the account, user can use the same license in same account or in different account'
  },
  {
    qId: 'insightFaqQ101',
    aId: 'insightFaqA101',
    defaultQ: 'Can I purchase subscriptions from mobile app?',
    defaultA: 'No. Users are redirected to web.'
  },
  {
    qId: 'insightFaqQ102',
    aId: 'insightFaqA102',
    defaultQ: 'Can I activate licenses from mobile app?',
    defaultA: 'No. Use web portal.'
  },
  {
    qId: 'insightFaqQ103',
    aId: 'insightFaqA103',
    defaultQ: 'What is Bundled (HB)?',
    defaultA: 'Device-based bundled subscription provided with hardware.'
  },
  {
    qId: 'insightFaqQ104',
    aId: 'insightFaqA104',
    defaultQ: 'What happens to existing Bundled (HB)?',
    defaultA: 'They continue until expiry.'
  },
  {
    qId: 'insightFaqQ105',
    aId: 'insightFaqA105',
    defaultQ: 'Why is my device unmanaged?',
    defaultA: `Possible reasons:
No available credit
Credit expired
Allocation not done
Subscription not active`
  },
  {
    qId: 'insightFaqQ106',
    aId: 'insightFaqA106',
    defaultQ: 'Why is my device online but not in Insight Cloud?',
    defaultA: 'Device is in Unmanaged state due to expired or missing credit.'
  },
  {
    qId: 'insightFaqQ107',
    aId: 'insightFaqA107',
    defaultQ: 'How do I fix unmanaged device issues?',
    defaultA: `Add valid credit
Move the device to Managed state (manually)
Allowing device to reboot after reboot the device should come to cloud connected state`
  }
];
